import React, { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { RISK_MESSAGE_COLOR_MAP } from "../../../constants";
import { getRiskMessageLabel } from "../../../helpers";
import RiskDescriptionContent from "../../../shared/riskDescription/RiskDescriptionContent";
import { hasRiskDescriptionContent } from "../../../../shared/riskDescriptionDict";
import AssessmentCardActions from "../../../shared/AssessmentCardActions";
import ExpandableText from "../../../shared/ExpandableText";
import {
  isAreaAssessment,
  getAreaTotals,
  getAreaPolygonMetrics,
} from "../../history/helpers";
import { formatArea, formatPerimeter } from "../../polygonDraw/helpers";

const getRiskColorByMessage = (message) => {
  if (!message) return "text.secondary";
  return RISK_MESSAGE_COLOR_MAP[message] || "text.secondary";
};

const toPercentage = (value) => {
  if (!Number.isFinite(value)) return "-";
  return `${Math.round(value * 100)}%`;
};

const formatCreationTime = (value, locale) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(locale || "en", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getTitle = (assessment) => {
  if (assessment?.name) return assessment?.name;
  if (assessment?.address) return assessment?.address;
  if (assessment?.egsa87x && assessment?.egsa87y)
    return `${assessment?.egsa87x}, ${assessment?.egsa87y}`;
  if (assessment?.latitude && assessment?.longitude)
    return `${assessment?.latitude}, ${assessment?.longitude}`;
  return "-";
};

const getCoordinatesText = (assessment) => {
  if (assessment?.latitude && assessment?.longitude)
    return `${assessment.latitude}, ${assessment.longitude}`;
  return "";
};

const getEgsa87CoordinatesText = (assessment) => {
  if (assessment?.egsa87x && assessment?.egsa87y)
    return `${assessment?.egsa87x}, ${assessment?.egsa87y}`;
  return "";
};

const RiskValueLabel = ({ label, risk, message, t }) => {
  const percentage = toPercentage(risk);
  const translatedMessage = message
    ? getRiskMessageLabel(message, t)
    : t("common.noData");
  const tooltipText = `${translatedMessage} (${percentage})`;

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {label}:
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: getRiskColorByMessage(message), fontWeight: 600 }}
        >
          {percentage}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AssessmentCard = memo(function AssessmentCard({
  assessment,
  onClick,
  onDownloadPdf,
  isDownloadingPdf,
  onEdit,
}) {
  const { t, i18n } = useTranslation();
  const [showDescriptions, setShowDescriptions] = useState(false);

  const address = assessment?.address;
  const coordinatesText = getCoordinatesText(assessment);
  const egsa87CoordinatesText = getEgsa87CoordinatesText(assessment);
  const notes = assessment?.notes;

  const isArea = isAreaAssessment(assessment);
  const areaTotals = useMemo(
    () => (isArea ? getAreaTotals(assessment) : null),
    [isArea, assessment],
  );
  const areaMetrics = useMemo(
    () => (isArea ? getAreaPolygonMetrics(assessment) : []),
    [isArea, assessment],
  );

  const fireRisk = Number(assessment?.fire?.risk);
  const floodRisk = Number(assessment?.flood?.risk);
  const earthquakeRisk = Number(assessment?.earthquake?.risk);
  const fireMessage = assessment?.fire?.message;
  const floodMessage = assessment?.flood?.message;
  const earthquakeMessage = assessment?.earthquake?.message;
  const createdAtIso = assessment?.creation_time || "";
  const localizedDate = useMemo(
    () =>
      formatCreationTime(createdAtIso, i18n.resolvedLanguage || i18n.language),
    [createdAtIso, i18n.language, i18n.resolvedLanguage],
  );

  const fireDescription = assessment?.fire?.description;
  const floodDescription = assessment?.flood?.description;
  const earthquakeDescription = assessment?.earthquake?.description;
  const fireDescriptionDict = assessment?.fire?.description_dict;
  const floodDescriptionDict = assessment?.flood?.description_dict;
  const earthquakeDescriptionDict = assessment?.earthquake?.description_dict;
  const fireHasDescription = useMemo(
    () =>
      hasRiskDescriptionContent({
        description: fireDescription,
        descriptionDict: fireDescriptionDict,
        riskType: "fire",
      }),
    [fireDescription, fireDescriptionDict],
  );
  const floodHasDescription = useMemo(
    () =>
      hasRiskDescriptionContent({
        description: floodDescription,
        descriptionDict: floodDescriptionDict,
        riskType: "flood",
      }),
    [floodDescription, floodDescriptionDict],
  );
  const earthquakeHasDescription = useMemo(
    () =>
      hasRiskDescriptionContent({
        description: earthquakeDescription,
        descriptionDict: earthquakeDescriptionDict,
        riskType: "earthquake",
      }),
    [earthquakeDescription, earthquakeDescriptionDict],
  );
  const hasDescriptions =
    fireHasDescription || floodHasDescription || earthquakeHasDescription;

  const handleExpandClick = (event) => {
    event.stopPropagation();
    setShowDescriptions((prev) => !prev);
  };

  const handleDownload = () => {
    if (onDownloadPdf) {
      onDownloadPdf(assessment);
    }
  };

  return (
    <Card
      onClick={() => onClick?.(assessment)}
      sx={{
        p: 1.5,
        position: "relative",
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #eee",
        boxShadow: "none",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.02)",
        },
      }}
    >
      <AssessmentCardActions
        latitude={assessment?.latitude}
        longitude={assessment?.longitude}
        onDownload={handleDownload}
        isDownloadingPdf={isDownloadingPdf}
        onEdit={onEdit ? () => onEdit(assessment) : undefined}
        showStreetView={!isArea}
      />
      <CardContent sx={{ padding: "0px !important" }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, pr: "66px", mb: 0.75 }}
        >
          {isArea
            ? assessment?.name || t("risksMap.polygon.areaTitle")
            : getTitle(assessment)}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("common.date")}
          </Typography>
          <Tooltip
            title={new Date(createdAtIso).toLocaleString() || "-"}
            arrow
            placement="top"
          >
            <Typography
              variant="body2"
              sx={{ cursor: createdAtIso ? "help" : "default" }}
            >
              {localizedDate}
            </Typography>
          </Tooltip>
        </Stack>

        {address ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("forms.address")}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: "right" }}>
              {address}
            </Typography>
          </Stack>
        ) : null}

        {coordinatesText && !isArea ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("forms.coordinates")}
            </Typography>
            <Typography variant="body2">{coordinatesText}</Typography>
          </Stack>
        ) : null}

        {egsa87CoordinatesText ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("forms.egsa87")}
            </Typography>
            <Typography variant="body2">{egsa87CoordinatesText}</Typography>
          </Stack>
        ) : null}

        {assessment?.floor ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("forms.floor")}
            </Typography>
            <Typography variant="body2">{assessment.floor}</Typography>
          </Stack>
        ) : null}

        {notes ? (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 0.25 }}
            >
              {t("forms.notes")}
            </Typography>
            <ExpandableText text={notes} maxLines={2} />
          </Box>
        ) : null}

        {isArea && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mt: 0.5 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("risksMap.polygon.areaLabel")}
              </Typography>
              <Typography variant="body2">
                {formatArea(areaTotals.area, t)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mt: 0.5 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("risksMap.polygon.perimeterLabel")}
              </Typography>
              <Typography variant="body2">
                {formatPerimeter(areaTotals.perimeter, t)}
              </Typography>
            </Stack>

            {areaMetrics.length > 1 &&
              areaMetrics.map((metric, index) => (
                <Stack
                  key={metric.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  sx={{ mt: 0.5, pl: 1 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("risksMap.polygon.polygonLabel")} {index + 1}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {formatArea(metric.area, t)} ·{" "}
                    {formatPerimeter(metric.perimeter, t)}
                  </Typography>
                </Stack>
              ))}
          </>
        )}

        <Stack direction="row" alignItems="center" spacing={2} mt={2}>
          <RiskValueLabel
            label={t("risks.fire")}
            risk={fireRisk}
            message={fireMessage}
            t={t}
          />
          <RiskValueLabel
            label={t("risks.flood")}
            risk={floodRisk}
            message={floodMessage}
            t={t}
          />
          <RiskValueLabel
            label={t("risks.earthquake")}
            risk={earthquakeRisk}
            message={earthquakeMessage}
            t={t}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginLeft: "auto !important",
            }}
          >
            {hasDescriptions && (
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: showDescriptions
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                  p: 0.5,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Stack>

        {hasDescriptions && (
          <Collapse in={showDescriptions} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5, pl: 0.5 }}>
              {fireHasDescription && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {t("portfolioMap.fireRiskSection")}
                  </Typography>
                  <RiskDescriptionContent
                    riskType="fire"
                    description={fireDescription}
                    descriptionDict={fireDescriptionDict}
                    dense
                  />
                </Box>
              )}
              {floodHasDescription && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {t("portfolioMap.floodRiskSection")}
                  </Typography>
                  <RiskDescriptionContent
                    riskType="flood"
                    description={floodDescription}
                    descriptionDict={floodDescriptionDict}
                    risk={floodRisk}
                    dense
                  />
                </Box>
              )}
              {earthquakeHasDescription && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {t("portfolioMap.earthquakeRiskSection")}
                  </Typography>
                  <RiskDescriptionContent
                    riskType="earthquake"
                    description={earthquakeDescription}
                    descriptionDict={earthquakeDescriptionDict}
                    dense
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
});

export default AssessmentCard;
